/**
 * Unify RelatedPerson — extends FHIR R4 RelatedPerson.
 *
 * A contact-only record of a person related to a patient: a family or couples
 * therapy participant (parent, spouse) attached to an episode, or an insurance
 * `Coverage.subscriber` who is not the patient. A RelatedPerson has no Patient
 * profile, ProjectMembership, invitation, login, or portal access, and no
 * guarantor or billing `Account` linkage (guarantor functionality was removed).
 *
 * ## Standard FHIR fields used by Unify
 *
 * | Field | Usage |
 * |-------|-------|
 * | `patient` | Reference → Patient the person is related to (the therapy client) |
 * | `relationship[]` | CodeableConcept from `v3-RoleCode` (see `UnifyRelationshipCode`) |
 * | `name[0]` | HumanName (given + family) |
 * | `identifier[]` | Seed tag identifier |
 * | `active` | Whether this relationship link is active |
 *
 * ## Relationship codes
 *
 * System: `http://terminology.hl7.org/CodeSystem/v3-RoleCode`
 *
 * | Code | Display | Typical usage |
 * |------|---------|---------------|
 * | `PRN` | Parent | Generic parent |
 * | `FTH` | Father | Father in family therapy |
 * | `MTH` | Mother | Mother in family therapy |
 * | `SPS` | Spouse | Partner in couples therapy |
 * | `GUARD` | Guardian | Legal guardian (a relationship label only; no financial meaning) |
 *
 * ## Insurance subscriber (not a guarantor)
 *
 * | Resource | Field | Purpose |
 * |----------|-------|---------|
 * | `Coverage` | `subscriber` → RelatedPerson | Insurance subscriber who is not the patient |
 *
 * ## Therapy participation linkage
 *
 * | Resource | Field | Purpose |
 * |----------|-------|---------|
 * | `EpisodeOfCare` | `extension[participants]` → RelatedPerson | Contact listed on the episode |
 * | `Appointment` | `participant[].actor` → RelatedPerson, `type = PART` | Supporting participant (not billed) |
 * | `Consent` | `provision.actor[].reference` → RelatedPerson | Consent for family/couples access |
 *
 * No Unify-specific custom extensions on RelatedPerson itself. Standard
 * FHIR fields are sufficient. Episode participants do not get a portal
 * membership and do not log in.
 */
import type { RelatedPerson, Patient, Reference, CodeableConcept } from '@medplum/fhirtypes';
import type { CoverageRelationship } from './coverage';

// ─── Relationship codes ─────────────────────────────────────────────

export const RELATIONSHIP_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-RoleCode';

export type UnifyRelationshipCode = 'PRN' | 'FTH' | 'MTH' | 'SPS' | 'GUARD';

const RELATIONSHIP_DISPLAY: Record<UnifyRelationshipCode, string> = {
  PRN: 'parent',
  FTH: 'Father',
  MTH: 'Mother',
  SPS: 'Spouse',
  GUARD: 'guardian',
};

// ─── Getters ────────────────────────────────────────────────────────

export function getRelatedPatientRef(resource: RelatedPerson): Reference<Patient> {
  return resource.patient;
}

export function getRelationshipCodes(resource: RelatedPerson): UnifyRelationshipCode[] {
  return (resource.relationship ?? [])
    .flatMap((r) => r.coding ?? [])
    .filter((c) => c.system === RELATIONSHIP_CODE_SYSTEM)
    .map((c) => c.code as UnifyRelationshipCode)
    .filter(Boolean);
}

export function hasRelationship(resource: RelatedPerson, code: UnifyRelationshipCode): boolean {
  return getRelationshipCodes(resource).includes(code);
}

// ─── Setters ────────────────────────────────────────────────────────

export function setRelatedPatientRef(resource: RelatedPerson, ref: Reference<Patient>): RelatedPerson {
  return { ...resource, patient: ref };
}

export function buildRelationship(code: UnifyRelationshipCode): CodeableConcept {
  return {
    coding: [{
      system: RELATIONSHIP_CODE_SYSTEM,
      code,
      display: RELATIONSHIP_DISPLAY[code],
    }],
  };
}

export function setRelationship(resource: RelatedPerson, code: UnifyRelationshipCode): RelatedPerson {
  return { ...resource, relationship: [buildRelationship(code)] };
}

export function addRelationship(resource: RelatedPerson, code: UnifyRelationshipCode): RelatedPerson {
  const existing = resource.relationship ?? [];
  return { ...resource, relationship: [...existing, buildRelationship(code)] };
}

// ─── Coverage subscriber (dependent) ────────────────────────────────

/**
 * Maps a Coverage subscriber-relationship (relationship of the patient to the
 * subscriber) to the RelatedPerson's role relative to the patient: a `child`
 * patient has a parent subscriber, a `spouse` patient has a spouse subscriber.
 */
const COVERAGE_TO_RELATIONSHIP: Record<Exclude<CoverageRelationship, 'self'>, UnifyRelationshipCode> = {
  spouse: 'SPS',
  child: 'PRN',
  other: 'GUARD',
};

export interface BuildSubscriberRelatedPersonParams {
  firstName: string;
  lastName: string;
  /** Subscriber date of birth (YYYY-MM-DD). */
  dob?: string;
  /** The covered patient this subscriber is related to. */
  patientRef: Reference<Patient>;
  /** Coverage relationship of the patient to the subscriber (never "self"). */
  relationship: Exclude<CoverageRelationship, 'self'>;
}

/** Builds a RelatedPerson representing an insurance subscriber who is not the patient. */
export function buildSubscriberRelatedPerson(
  params: BuildSubscriberRelatedPersonParams,
): RelatedPerson {
  return {
    resourceType: 'RelatedPerson',
    active: true,
    patient: params.patientRef,
    name: [{ given: [params.firstName], family: params.lastName }],
    ...(params.dob ? { birthDate: params.dob } : {}),
    relationship: [buildRelationship(COVERAGE_TO_RELATIONSHIP[params.relationship])],
  };
}
